import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, X, Image as ImageIcon, Loader2, AlertCircle } from 'lucide-react';
import { Button, cn } from '@tendedloop/ui';
import {
  compressImage,
  getImagePreviewUrl,
  formatFileSize,
  isValidImageType,
  MAX_FILE_SIZE,
} from '@/lib/imageCompression';

interface PhotoCaptureProps {
  onCapture: (file: File) => void;
  capturedImage: File | null;
  onRemove: () => void;
  disabled?: boolean;
  className?: string;
}

export function PhotoCapture({ onCapture, capturedImage, onRemove, disabled = false, className }: PhotoCaptureProps) {
  const { t } = useTranslation('visitor');
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate preview when image changes
  useEffect(() => {
    if (capturedImage) {
      getImagePreviewUrl(capturedImage)
        .then(setPreviewUrl)
        .catch(() => setPreviewUrl(null));
    } else {
      setPreviewUrl(null);
    }
  }, [capturedImage]);

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be selected again
    event.target.value = '';

    setError(null);

    // Validate file type
    if (!isValidImageType(file)) {
      setError(t('photo.invalidType'));
      return;
    }

    // Validate file size (before compression)
    if (file.size > MAX_FILE_SIZE) {
      setError(t('photo.tooLarge'));
      return;
    }

    setIsProcessing(true);

    try {
      // Compress the image
      const compressedFile = await compressImage(file);
      onCapture(compressedFile);
    } catch (err) {
      console.error('Failed to process image:', err);
      setError(t('photo.processingFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Open camera/file picker
  const handleCaptureClick = () => {
    inputRef.current?.click();
  };

  // Handle remove
  const handleRemove = () => {
    setPreviewUrl(null);
    setError(null);
    onRemove();
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        onChange={handleFileSelect}
        disabled={disabled || isProcessing}
        className="hidden"
        aria-label={t('photo.captureAriaLabel')}
      />

      {/* Preview or capture button */}
      {capturedImage && previewUrl ? (
        <div className="relative">
          {/* Image preview */}
          <div className="relative aspect-video w-full overflow-hidden rounded-lg border bg-muted">
            <img src={previewUrl} alt={t('photo.previewAlt')} className="h-full w-full object-cover" />
          </div>

          {/* File info */}
          <p className="mt-1 text-[15px] text-muted-foreground text-center">{formatFileSize(capturedImage.size)}</p>

          {/* Remove button - 48px minimum touch target */}
          <Button
            variant="destructive"
            size="icon"
            className="absolute -top-3 -end-3 h-12 w-12 rounded-full shadow-md"
            onClick={handleRemove}
            disabled={disabled}
            aria-label={t('photo.removeAriaLabel')}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleCaptureClick}
          disabled={disabled || isProcessing}
          className={cn(
            'flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors',
            'hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
            disabled && 'cursor-not-allowed opacity-50',
            isProcessing && 'cursor-wait',
          )}
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="text-[15px] text-muted-foreground">{t('photo.processing')}</span>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Camera className="h-6 w-6 text-muted-foreground" />
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              </div>
              <span className="text-[16px] font-medium">{t('feedback.addPhoto')}</span>
              <span className="text-[15px] text-muted-foreground">{t('feedback.addPhotoHelper')}</span>
            </>
          )}
        </button>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-[15px] text-destructive">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
